import {Avatar, AvatarGroup, Badge, Skeleton, Typography} from '@sollapay/ui/components';
import {Trans, useTranslation} from 'react-i18next';

import {FoldButton, FoldChevron} from '@/components';
import {getInitials} from '@/utils';

import type {GuestAccountInviter} from '../utils';
import type {GuestSharedTrustAccountDTO} from '@sollapay/types';
import type {FC} from 'react';

export interface GuestFirmHeaderProps {
  firm: GuestSharedTrustAccountDTO['firm'];
  accountCount: number;
  inviters: GuestAccountInviter[];
  headingId: string;
  panelId: string;
  isCollapsed?: boolean;
  onToggle: () => void;
}

/** The name's fold button is stretched over the whole bar, so a click anywhere toggles the card. */
export const GuestFirmHeader: FC<GuestFirmHeaderProps> = ({
  firm,
  accountCount,
  inviters,
  isCollapsed = false,
  onToggle,
  ...ids
}) => {
  const {t} = useTranslation('guest');
  const name = firm.displayName || t('accounts.firm.unknown');

  return (
    <div
      data-slot="guest-firm-header"
      className="relative flex flex-wrap items-center gap-4 bg-background-selected px-5 py-4 transition-colors hover:bg-background-selected-strong"
    >
      <Avatar
        initials={getInitials(name)}
        src={firm.logoUrl ?? undefined}
        alt=""
        shape="square"
        size="lg"
        seed={firm.id}
      />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2.5">
          <FirmTitle {...ids} name={name} isCollapsed={isCollapsed} onToggle={onToggle} />
          <Badge variant="surface" size="sm">
            {t('accounts.accountCount', {count: accountCount})}
          </Badge>
        </div>
        {inviters.length > 0 && <InviterLine inviters={inviters} />}
      </div>
      <FoldChevron isCollapsed={isCollapsed} className="size-5 shrink-0 text-fg-tertiary" />
    </div>
  );
};

export const GuestFirmHeaderSkeleton: FC = () => (
  <div
    data-slot="guest-firm-header-skeleton"
    className="flex items-center gap-4 bg-background-selected px-5 py-4"
  >
    <Skeleton className="size-10 rounded-md" />
    <div className="flex flex-1 flex-col gap-2">
      <Skeleton className="h-5 w-48" />
      <Skeleton className="h-4 w-36" />
    </div>
  </div>
);

type FirmTitleProps = Pick<GuestFirmHeaderProps, 'headingId' | 'panelId' | 'onToggle'> & {
  name: string;
  isCollapsed: boolean;
};

const FirmTitle: FC<FirmTitleProps> = ({name, headingId, panelId, isCollapsed, onToggle}) => (
  <h2 id={headingId} className="min-w-0 font-display text-lg font-bold text-fg">
    <FoldButton
      isCollapsed={isCollapsed}
      controlsId={panelId}
      onToggle={onToggle}
      className="justify-start overflow-visible whitespace-normal text-start text-lg"
    >
      {name}
    </FoldButton>
  </h2>
);

interface InviterLineProps {
  inviters: GuestAccountInviter[];
}

const InviterLine: FC<InviterLineProps> = ({inviters}) => {
  const {t} = useTranslation('guest');
  const [first] = inviters;

  return (
    <div data-slot="guest-firm-inviter" className="flex min-w-0 items-center gap-1.5">
      <AvatarGroup size="compact">
        {inviters.map(({id, name, picture}) => (
          <Avatar
            key={id}
            initials={getInitials(name)}
            src={picture ?? undefined}
            alt=""
            size="compact"
            seed={id}
            noShadow
          />
        ))}
      </AvatarGroup>
      <Typography size="sm" color="tertiary">
        <Trans
          t={t}
          i18nKey={
            inviters.length === 1 ? 'accounts.firm.invitedBy' : 'accounts.firm.invitedByCount'
          }
          values={{
            name: first?.name && t('accounts.inviterName', {name: first.name}),
            count: inviters.length
          }}
          components={{b: <strong className="font-medium text-fg-secondary" />}}
        />
      </Typography>
    </div>
  );
};
